<template>
    <div class="p-6">
        <h1 class="text-2xl font-bold mb-4">Správa kategórií predmetov podnikania</h1>
        <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-2">
            <div class="flex gap-2 w-full md:w-auto">
                <input v-model="search" type="text" placeholder="Hľadať..."
                    class="border rounded px-2 py-1 w-full md:w-64" />
                <button v-if="search" @click="resetSearch"
                    class="px-2 py-1 bg-gray-200 rounded text-gray-700">Reset</button>
            </div>
            <button class="px-4 py-2 bg-teal-600 text-white rounded" @click="openAddModal">Pridať novú
                kategóriu</button>
        </div>
        <table class="min-w-full bg-white border">
            <thead>
                <tr>
                    <th class="px-4 py-2 border cursor-pointer" @click="sortBy('id')">
                        ID
                        <span v-if="sort.key === 'id'">{{ sort.asc ? '▲' : '▼' }}</span>
                    </th>
                    <th class="px-4 py-2 border cursor-pointer" @click="sortBy('title')">
                        Názov
                        <span v-if="sort.key === 'title'">{{ sort.asc ? '▲' : '▼' }}</span>
                    </th>
                    <th class="px-4 py-2 border cursor-pointer" @click="sortBy('parent_id')">
                        Nadradená kategória
                        <span v-if="sort.key === 'parent_id'">{{ sort.asc ? '▲' : '▼' }}</span>
                    </th>
                    <th class="px-4 py-2 border">Akcie</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="cat in sortedCategories" :key="cat.id">
                    <td class="border px-4 py-2">{{ cat.id }}</td>
                    <td class="border px-4 py-2">{{ cat.title }}</td>
                    <td class="border px-4 py-2">{{ getParentTitle(cat.parent_id ?? null) }}</td>
                    <td class="border px-4 py-2">
                        <button class="mr-2 px-2 py-1 bg-yellow-400 rounded"
                            @click="openEditModal(cat)">Upraviť</button>
                        <button class="px-2 py-1 bg-red-500 text-white rounded"
                            @click="deleteCategory(cat.id)">Vymazať</button>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Add/Edit Modal -->
        <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
            <div class="bg-white p-6 rounded shadow-lg w-full max-w-md">
                <h2 class="text-xl font-bold mb-4">{{ editMode ? 'Upraviť' : 'Pridať' }} kategóriu</h2>
                <form @submit.prevent="editMode ? updateCategory() : addCategory()">
                    <div class="mb-2">
                        <label class="block mb-1">Názov</label>
                        <input v-model="form.title" class="w-full border rounded px-2 py-1" required />
                    </div>
                    <div class="mb-2">
                        <label class="block mb-1">Nadradená kategória</label>
                        <select v-model="form.parent_id" class="w-full border rounded px-2 py-1">
                            <option :value="null">Žiadna</option>
                            <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                                {{ cat.title }}
                            </option>
                        </select>
                    </div>
                    <div class="flex justify-end gap-2 mt-4">
                        <button type="button" class="px-4 py-2 bg-gray-300 rounded" @click="closeModal">Zrušiť</button>
                        <button type="submit" class="px-4 py-2 bg-teal-600 text-white rounded">Uložiť</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { toast } from 'vue3-toastify';
import SubjectOfBusinessCategoryDataService from '@/services/SubjectOfBusinessCategoryDataService';

interface Category {
    id: number;
    title: string;
    parent_id?: number | null;
}

const categories = ref<Category[]>([]);
const search = ref('');
const showModal = ref(false);
const editMode = ref(false);
const form = ref<{ id: number | null; title: string; parent_id: number | null }>({ id: null, title: '', parent_id: null });
const sort = reactive({ key: 'id', asc: true });

function resetSearch() {
    search.value = '';
}

function sortBy(key: keyof Category) {
    if (sort.key === key) {
        sort.asc = !sort.asc;
    } else {
        sort.key = key;
        sort.asc = true;
    }
}

const filteredCategories = computed(() => {
    if (!search.value) return categories.value;
    const s = search.value.toLowerCase();
    return categories.value.filter((item) =>
        (item.title && item.title.toLowerCase().includes(s)) ||
        (item.id && String(item.id).includes(s))
    );
});

const sortedCategories = computed(() => {
    const arr = [...filteredCategories.value];
    arr.sort((a, b) => {
        let aVal = a[sort.key as keyof Category];
        let bVal = b[sort.key as keyof Category];
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';
        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return sort.asc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sort.asc ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
    });
    return arr;
});

function getParentTitle(parent_id: number | null) {
    if (!parent_id) return 'Žiadna';
    const parent = categories.value.find((cat) => cat.id === parent_id);
    return parent ? parent.title : 'Žiadna';
}

function openAddModal() {
    editMode.value = false;
    form.value = { id: null, title: '', parent_id: null };
    showModal.value = true;
}

function openEditModal(cat: Category) {
    editMode.value = true;
    form.value = { id: cat.id, title: cat.title, parent_id: cat.parent_id ?? null };
    showModal.value = true;
}

function closeModal() {
    showModal.value = false;
}

async function fetchData() {
    const res = await SubjectOfBusinessCategoryDataService.getAllCategories();
    categories.value = res.data.data;
}

function extractErrorMessage(error: unknown, prefix = ''): string {
    const err = error as any;
    if (
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'message' in err.response.data &&
        typeof err.response.data.message === 'string'
    ) {
        return prefix + err.response.data.message;
    } else if (error instanceof Error) {
        return prefix + error.message;
    } else {
        return prefix + String(error);
    }
}

async function addCategory() {
    // Exclude id from payload for add
    const { id, ...payload } = form.value;
    if (!payload.parent_id) payload.parent_id = null;
    try {
        await SubjectOfBusinessCategoryDataService.add(payload);
        await fetchData();
        closeModal();
        toast.success('Kategória bola úspešne pridaná.');
    } catch (error) {
        toast.error(extractErrorMessage(error, 'Chyba pri pridávaní kategórie: '));
    }
}

async function updateCategory() {
    const payload = { title: form.value.title, parent_id: form.value.parent_id };
    if (!payload.parent_id) payload.parent_id = null;
    try {
        await SubjectOfBusinessCategoryDataService.update(form.value.id, payload);
        await fetchData();
        closeModal();
        toast.success('Kategória bola úspešne upravená.');
    } catch (error) {
        toast.error(extractErrorMessage(error, 'Chyba pri úprave kategórie: '));
    }
}

async function deleteCategory(id: number) {
    if (confirm('Naozaj chcete vymazať túto kategóriu?')) {
        try {
            await SubjectOfBusinessCategoryDataService.delete(id);
            await fetchData();
            toast.success('Kategória bola úspešne vymazaná.');
        } catch (error) {
            toast.error(extractErrorMessage(error, 'Chyba pri mazaní kategórie: '));
        }
    }
}

onMounted(fetchData);
</script>
